import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";
import SortIcon from "@mui/icons-material/Sort";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import api from "../../../conf/api";

/** Every filter the user list understands, in its "nothing selected" state. */
export const EMPTY_FILTERS = {
  search: "",
  ordering: "recent",
  is_verified: "",
  is_active: "",
  deleted: "",
  has_contract: "",
  has_paypal: "",
  country: "",
  category: "",
  joined_from: "",
  joined_to: "",
  revenue_min: "",
  revenue_max: "",
};

const SORT_OPTIONS = [
  { value: "recent", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "name", label: "Name (A–Z)" },
  { value: "email", label: "Email (A–Z)" },
  { value: "id", label: "User ID" },
  { value: "country", label: "Country" },
  { value: "revenue_high", label: "Revenue (high → low)" },
  { value: "revenue_low", label: "Revenue (low → high)" },
];

/**
 * One-click toggles for the states an admin filters by constantly. Each flips
 * between "off" and one specific value rather than cycling three ways — a
 * tri-state chip reads as ambiguous, and the inverse of each is available in
 * the advanced panel.
 */
const QUICK_FILTERS = [
  { key: "is_verified", value: "true", label: "Verified", color: "success" },
  { key: "is_verified", value: "false", label: "Unverified", color: "warning" },
  { key: "has_contract", value: "true", label: "Has contract", color: "info" },
  { key: "has_contract", value: "false", label: "No contract", color: "default" },
  { key: "is_active", value: "false", label: "Inactive", color: "default" },
  { key: "deleted", value: "true", label: "Deleted", color: "error" },
];

/** Human labels for the active-filter chips. */
const CHIP_LABELS = {
  search: (v) => `Search: “${v}”`,
  is_verified: (v) => (v === "true" ? "Verified" : "Unverified"),
  is_active: (v) => (v === "true" ? "Active" : "Inactive"),
  deleted: (v) => (v === "true" ? "Deleted" : "Not deleted"),
  has_contract: (v) => (v === "true" ? "Has contract" : "No contract"),
  has_paypal: (v) => (v === "true" ? "Has PayPal" : "No PayPal"),
  country: (v) => `Country: ${v}`,
  category: (v, opts) => {
    const hit = opts.categories.find((c) => String(c.id) === String(v));
    return `Category: ${hit ? hit.name : v}`;
  },
  joined_from: (v) => `Joined after ${v}`,
  joined_to: (v) => `Joined before ${v}`,
  revenue_min: (v) => `Revenue ≥ ${v}%`,
  revenue_max: (v) => `Revenue ≤ ${v}%`,
};

/** Count of filters actually narrowing the list (sort is not a filter). */
export function activeFilterCount(filters) {
  return Object.entries(filters).filter(
    ([key, value]) => key !== "ordering" && value !== "" && value != null
  ).length;
}

/** Strip empty values so the request URL only carries what is really set. */
export function toQueryParams(filters) {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (value !== "" && value != null) acc[key] = value;
    return acc;
  }, {});
}

export default function UserFilters({ filters, onChange, resultCount, loading }) {
  const [expanded, setExpanded] = useState(false);
  const [options, setOptions] = useState({ countries: [], categories: [] });
  // The search box is uncontrolled-by-parent so typing stays instant; the
  // value is pushed up on a debounce, otherwise every keystroke is a request.
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const debounceRef = useRef(null);
  const activeCount = activeFilterCount(filters);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/users/filter-options/")
      .then((res) => {
        if (!cancelled) setOptions(res.data);
      })
      .catch(() => {
        // Options are a convenience — a failure here must not take the whole
        // list down, so the dropdowns just stay empty and search still works.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the box in step when the parent resets filters from outside.
  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  const set = useCallback(
    (key, value) => onChange({ ...filters, [key]: value }),
    [filters, onChange]
  );

  const handleSearch = useCallback(
    (event) => {
      const next = event.target.value;
      setSearchDraft(next);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onChange({ ...filters, search: next });
      }, 350);
    },
    [filters, onChange]
  );

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const toggleQuick = useCallback(
    (quick) => {
      const isOn = filters[quick.key] === quick.value;
      set(quick.key, isOn ? "" : quick.value);
    },
    [filters, set]
  );

  const activeChips = useMemo(
    () =>
      Object.entries(filters)
        .filter(([key, value]) => key !== "ordering" && value !== "" && value != null)
        .map(([key, value]) => ({
          key,
          label: CHIP_LABELS[key] ? CHIP_LABELS[key](value, options) : `${key}: ${value}`,
        })),
    [filters, options]
  );

  const reset = useCallback(() => {
    clearTimeout(debounceRef.current);
    setSearchDraft("");
    onChange({ ...EMPTY_FILTERS });
  }, [onChange]);

  const fieldSx = { minWidth: 160, flex: "1 1 160px" };

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        p: 1.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: activeCount ? "primary.light" : "grey.300",
        transition: "border-color .2s ease",
      }}
    >
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="center">
        <TextField
          size="small"
          fullWidth
          placeholder="Search by ID, name, email, PayPal or phone…"
          value={searchDraft}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchDraft ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchDraft("");
                    clearTimeout(debounceRef.current);
                    onChange({ ...filters, search: "" });
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
            sx: { borderRadius: 6, bgcolor: "grey.50" },
          }}
        />

        <TextField
          select
          size="small"
          value={filters.ordering}
          onChange={(e) => set("ordering", e.target.value)}
          sx={{ minWidth: 190 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SortIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            sx: { borderRadius: 6 },
          }}
        >
          {SORT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        <Badge badgeContent={activeCount} color="primary">
          <Button
            size="medium"
            variant={expanded ? "contained" : "outlined"}
            startIcon={<TuneIcon />}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? "rotate(180deg)" : "none",
                  transition: "transform .2s ease",
                }}
              />
            }
            onClick={() => setExpanded((v) => !v)}
            sx={{ borderRadius: 6, textTransform: "none", whiteSpace: "nowrap" }}
          >
            Filters
          </Button>
        </Badge>

        <Tooltip title="Reload">
          <span>
            <IconButton onClick={() => onChange({ ...filters })} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1.5} alignItems="center">
        {QUICK_FILTERS.map((quick) => {
          const isOn = filters[quick.key] === quick.value;
          return (
            <Chip
              key={`${quick.key}-${quick.value}`}
              label={quick.label}
              size="small"
              clickable
              color={isOn ? quick.color : "default"}
              variant={isOn ? "filled" : "outlined"}
              onClick={() => toggleQuick(quick)}
              sx={{ fontWeight: isOn ? 600 : 400 }}
            />
          );
        })}
        <Box flexGrow={1} />
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          {loading
            ? "Loading…"
            : `${resultCount} ${resultCount === 1 ? "user" : "users"}${
                activeCount ? " matching" : ""
              }`}
        </Typography>
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" flexWrap="wrap" gap={1.5}>
          <Autocomplete
            size="small"
            sx={fieldSx}
            options={options.countries || []}
            getOptionLabel={(o) => (typeof o === "string" ? o : o.value)}
            isOptionEqualToValue={(o, v) =>
              (typeof o === "string" ? o : o.value) === (typeof v === "string" ? v : v?.value)
            }
            value={filters.country || null}
            onChange={(_, next) =>
              set("country", next ? (typeof next === "string" ? next : next.value) : "")
            }
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                <Box display="flex" justifyContent="space-between" width="100%">
                  <span>{option.value}</span>
                  <Typography variant="caption" color="text.secondary">
                    {option.count}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Country" />}
          />

          <TextField
            select
            size="small"
            label="Category"
            sx={fieldSx}
            value={filters.category}
            onChange={(e) => set("category", e.target.value)}
          >
            <MenuItem value="">Any category</MenuItem>
            {(options.categories || []).map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name} ({c.count})
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="PayPal"
            sx={fieldSx}
            value={filters.has_paypal}
            onChange={(e) => set("has_paypal", e.target.value)}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="true">Has PayPal email</MenuItem>
            <MenuItem value="false">Missing PayPal email</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label="Account state"
            sx={fieldSx}
            value={filters.is_active}
            onChange={(e) => set("is_active", e.target.value)}
          >
            <MenuItem value="">Any</MenuItem>
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </TextField>

          <TextField
            size="small"
            type="date"
            label="Joined after"
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            value={filters.joined_from}
            onChange={(e) => set("joined_from", e.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label="Joined before"
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            value={filters.joined_to}
            onChange={(e) => set("joined_to", e.target.value)}
          />

          <TextField
            size="small"
            type="number"
            label="Revenue min %"
            sx={fieldSx}
            inputProps={{ min: 0, max: 100 }}
            value={filters.revenue_min}
            onChange={(e) => set("revenue_min", e.target.value)}
          />
          <TextField
            size="small"
            type="number"
            label="Revenue max %"
            sx={fieldSx}
            inputProps={{ min: 0, max: 100 }}
            value={filters.revenue_max}
            onChange={(e) => set("revenue_max", e.target.value)}
          />
        </Stack>
      </Collapse>

      {activeCount > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
            {activeChips.map((chip) => (
              <Chip
                key={chip.key}
                size="small"
                label={chip.label}
                onDelete={() => set(chip.key, "")}
                sx={{ bgcolor: "primary.50", borderRadius: 2 }}
              />
            ))}
            <Button size="small" onClick={reset} sx={{ textTransform: "none" }}>
              Clear all
            </Button>
          </Stack>
        </>
      )}
    </Paper>
  );
}
